import React from 'react';
/**
 * useGanttJobs Hook - Manages Gantt chart jobs fetching and state
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import jobService from '../services/jobService';
import { STATUS_CONFIG } from '../constants/status';

export interface GanttJob {
  id: number;
  hospital_nome: string;
  hospital_nome_fantasia?: string; // Nome fantasia do hospital
  hospital_cliente_codigo?: string; // Código do hospital no cliente
  cliente_razao_social?: string;
  cliente_nome_fantasia?: string; // Nome fantasia do cliente
  cliente_id?: number;
  clientes?: Array<{id: number; razao_social: string; nome_fantasia?: string; codigo: string}>; // Epic 28: Multiple clients
  hospital_id: number;
  operador_id: number;
  operador_nome: string;
  titulo: string;
  status: string;
  data_prevista: string;
  hora_inicio?: string;
  hora_fim?: string;
  duracao?: number;
  total_items_counted: number;
  total_quantity_counted: number;
  auditores?: Array<{id: number; nome: string; email: string}>;
  locais_contagem?: Array<{id: number; nome: string}>;
}

interface UseGanttJobsReturn {
  jobs: GanttJob[];
  setJobs: React.Dispatch<React.SetStateAction<GanttJob[]>>;
  loading: boolean;
  fetchJobs: () => Promise<void>;
  filteredJobs: GanttJob[];
  stats: Record<string, number>;
  // Filter options
  hospitalOptions: Array<{ value: number; label: string }>;
  clienteOptions: Array<{ value: number; label: string }>;
  auditorOptions: Array<{ value: number; label: string }>;
  codHospitalClienteOptions: Array<{ value: string; label: string }>;
}

interface UseGanttJobsProps {
  selectedDate: Date;
  dateParam: string | null;
  selectedHospitals: (string | number)[];
  selectedClientes: (string | number)[];
  selectedAuditores: (string | number)[];
  selectedStatus: (string | number)[];
  selectedCodHospitalCliente: (string | number)[];
}

export function useGanttJobs({
  selectedDate,
  dateParam,
  selectedHospitals,
  selectedClientes,
  selectedAuditores,
  selectedStatus,
  selectedCodHospitalCliente,
}: UseGanttJobsProps): UseGanttJobsReturn {
  const [jobs, setJobs] = useState<GanttJob[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);

      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const firstPage = await jobService.getJobs({
        page: 1,
        page_size: 100,
        data_inicio: dateStr,
        data_fim: dateStr
      });
      let allJobs = [...firstPage.items];

      if (firstPage.total_pages > 1) {
        const remainingPages = Array.from(
          { length: firstPage.total_pages - 1 },
          (_, i) => i + 2
        );

        const otherPages = await Promise.all(
          remainingPages.map(page =>
            jobService.getJobs({
              page,
              page_size: 100,
              data_inicio: dateStr,
              data_fim: dateStr
            })
          )
        );

        otherPages.forEach(response => {
          allJobs = [...allJobs, ...response.items];
        });
      }

      setJobs(allJobs);
    } catch (err) {
      console.error('Error loading jobs:', err);
      toast.error('Erro ao carregar contagens');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Load jobs when date changes
  const dateDependency = dateParam || format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    fetchJobs();
  }, [dateDependency, fetchJobs]);

  // Extract unique filter options
  // #4: ordenar as opções dos filtros da agenda em ordem alfabética
  // (case-insensitive, pt-BR). Vale p/ hospital, cliente e auditor.
  const _ordenarPorLabel = (arr: Array<{ value: number; label: string }>) =>
    arr.sort((a, b) => (a.label || '').localeCompare(b.label || '', 'pt-BR', { sensitivity: 'base' }));

  const hospitalOptions = useMemo(() => {
    const seen = new Map<number, string>();
    jobs.forEach(j => {
      if (!seen.has(j.hospital_id)) {
        seen.set(j.hospital_id, j.hospital_nome);
      }
    });
    return _ordenarPorLabel(Array.from(seen.entries()).map(([value, label]) => ({ value, label })));
  }, [jobs]);

  const clienteOptions = useMemo(() => {
    const seen = new Map<number, string>();
    jobs.forEach(j => {
      const clienteDisplay = j.cliente_nome_fantasia || j.cliente_razao_social;
      if (j.cliente_id && clienteDisplay && !seen.has(j.cliente_id)) {
        seen.set(j.cliente_id, clienteDisplay);
      }
    });
    return _ordenarPorLabel(Array.from(seen.entries()).map(([value, label]) => ({ value, label })));
  }, [jobs]);

  const auditorOptions = useMemo(() => {
    const seen = new Map<number, string>();
    jobs.forEach(j => {
      if (!seen.has(j.operador_id)) {
        seen.set(j.operador_id, j.operador_nome);
      }
    });
    return _ordenarPorLabel(Array.from(seen.entries()).map(([value, label]) => ({ value, label })));
  }, [jobs]);

  const codHospitalClienteOptions = useMemo(() => {
    const seen = new Set<string>();
    jobs.forEach(j => {
      if (j.hospital_cliente_codigo && !seen.has(j.hospital_cliente_codigo)) {
        seen.add(j.hospital_cliente_codigo);
      }
    });
    return Array.from(seen)
      .sort((a, b) => {
        const numA = parseInt(a);
        const numB = parseInt(b);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
      })
      .map(code => ({ value: code, label: code }));
  }, [jobs]);

  // Filter jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const jobDateStr = job.data_prevista.split('T')[0];
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const selectedDateStr = `${year}-${month}-${day}`;

      if (jobDateStr !== selectedDateStr) return false;
      if (selectedHospitals.length > 0 && !selectedHospitals.includes(job.hospital_id)) return false;
      // #9: filtrar por cliente considerando TODOS os clientes do job (multi-
      // cliente), nao so o primario (job.cliente_id). Assim, filtrar por um
      // cliente traz tambem as contagens do hospital que tem esse cliente
      // junto com outros (antes sumiam por checar so o cliente_id primario).
      if (selectedClientes.length > 0) {
        const idsDoJob = new Set<number>();
        if (job.cliente_id) idsDoJob.add(job.cliente_id);
        (job.clientes || []).forEach((c) => { if (c?.id) idsDoJob.add(c.id); });
        const algumSelecionado = selectedClientes.some((cid) => idsDoJob.has(Number(cid)));
        if (!algumSelecionado) return false;
      }
      if (selectedAuditores.length > 0 && !selectedAuditores.includes(job.operador_id)) return false;
      if (selectedStatus.length > 0 && !selectedStatus.includes(job.status)) return false;
      if (selectedCodHospitalCliente.length > 0 && (!job.hospital_cliente_codigo || !selectedCodHospitalCliente.includes(job.hospital_cliente_codigo))) return false;

      return true;
    })
    // #6: ordenar os agendamentos do dia por HORARIO (mais cedo -> mais tarde),
    // em vez da ordem de criacao. Sem hora_inicio vai para o fim.
    .sort((a, b) => {
      const ha = a.hora_inicio || '99:99';
      const hb = b.hora_inicio || '99:99';
      return ha.localeCompare(hb);
    });
  }, [jobs, selectedDate, selectedHospitals, selectedClientes, selectedAuditores, selectedStatus, selectedCodHospitalCliente]);

  // Statistics
  const stats = useMemo(() => {
    const counts: Record<string, number> = { total: filteredJobs.length };

    Object.keys(STATUS_CONFIG).forEach(status => {
      counts[status] = 0;
    });

    const uniqueAuditores = new Set<number>();

    filteredJobs.forEach(job => {
      if (counts[job.status] !== undefined) {
        counts[job.status]++;
      }
      uniqueAuditores.add(job.operador_id);
    });

    counts['auditores'] = uniqueAuditores.size;

    return counts;
  }, [filteredJobs]);

  return {
    jobs,
    setJobs,
    loading,
    fetchJobs,
    filteredJobs,
    stats,
    hospitalOptions,
    clienteOptions,
    auditorOptions,
    codHospitalClienteOptions,
  };
}
